import { Trans } from "@lingui/react/macro";
import React, { useState, useCallback } from "react";
import { graphql, HeadProps, PageProps, Slice } from "gatsby";
import { ShipQuery } from "types/graphql-types";

import {
  Button,
  DescriptionTextColumnContent,
  DescriptionTextColumnContentLeft,
  DescriptionTextColumnContentRight,
  DescriptionTextColumnLeadIn,
  HeroBanner,
  HeroBannerRightPanel,
  HeroBannerTitle,
  HeroBannerAction,
  DescriptionTextColumn,
  SectionContainer,
  ButtonsContainer,
  Tabs,
  TabPanel,
  TabList,
  Tab
} from "@silversea-ssc/ui-components-react";
import FindYourCruise from "components/FindYourCruise";

import FYCQueryBuilder from "components/FindYourCruise/FYCQueryBuilder";
import TextBox from "components/TextBox/TextBox";
import { isPresent } from "src/utils/checks";
import ServiceRow, {
  ServiceRowContent,
  ServiceRowFooter,
  ServiceRowImages,
  ServiceRowTitle
} from "components/ServiceRow/ServiceRow";
import { BlockContent } from "components/BlockContent";
import PageBuilderButtonCTA from "components/PageBuilder/PageBuilderButtonCTA";
import ServiceRows from "components/ServiceRow/ServiceRows";
import { useDateOverride } from "src/utils/date/useDateOverride";
import { getCurrentVersionAt } from "src/utils/ship";
import { toUTCDate } from "src/utils/date";
import { PageContext } from "types/pageContext";
import GatsbyHeadOpenGraph from "components/SEO/GatsbyHeadOpenGraph";
import { ShipPageModalContents } from "./types";
import ShipVersionContent from "./ShipVersionContent";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import { validityDateLabel, versionsToValidityDates } from "./utils";
const ResponsiveSlideshow = React.lazy(
  () => import("components/Slideshow/ResponsiveSlideshow")
);
const ShipSuiteModal = React.lazy(
  () => import("components/ShipFacilityModal/ShipSuiteModal")
);
const ShipFacilityModal = React.lazy(
  () => import("components/ShipFacilityModal/ShipFacilityModal")
);

const ShipCruisesSlider = React.lazy(
  () => import("src/page-components/ship/ShipCruisesSlider")
);

type ShipPageProps = ShipQuery & {
  ship: NonNullable<ShipQuery["ship"]>;
};

const ShipTemplate: React.FC<
  React.PropsWithChildren<PageProps<ShipPageProps>>
> = (props) => {
  const ship = props.data.ship;
  const shipName = ship.name;
  const language = useLanguage();
  const fycQuery = new FYCQueryBuilder().filterByShipName(shipName).generate();

  const lastMinuteOfferCodes = props.data.cruisePromosContent
    .filter((promo) => promo.showDestinationCruiseSlider)
    .map((promo) => promo.configuration?.algoliaCode)
    .filter(isPresent);

  const isLastMinuteOfferActive =
    lastMinuteOfferCodes.length > 0 ? true : false;

  const versions = ship.versions?.filter(isPresent) || [];
  const now = useDateOverride() || toUTCDate(new Date());

  const currentVersion = getCurrentVersionAt(versions, now);
  if (!currentVersion) throw `No current version for ship ${shipName}`;

  const showVersionsTabs =
    versions.length > 1 || versions[0]?._id != currentVersion._id;
  const validityDates = versionsToValidityDates(versions, currentVersion);

  const brochuresUrl =
    currentVersion.brochureGroup?.fullPath || props.data.rabPagePath;

  const [openedModal, setOpenModal] =
    useState<ShipPageModalContents>(undefined);

  const [tabIndex, setTabIndex] = useState(0);
  const filteredSlideshow = currentVersion.slideshow?.filter(isPresent);

  const hasSpecialFeatureContent =
    currentVersion.specialFeatures?.length || 0 > 0;

  const onViewVoyagesClick = useCallback(() => {
    setTabIndex(0);
    const anchor = document.querySelector<HTMLDivElement>("#fyc");
    if (anchor) {
      const offsetTop = anchor.offsetTop;
      scroll({
        top: offsetTop,
        behavior: "smooth"
      });
    }
  }, []);

  return (
    <React.Fragment>
      <HeroBanner background={currentVersion.heroImage} overlay="right">
        <HeroBannerRightPanel>
          <HeroBannerTitle>{shipName}</HeroBannerTitle>
          <HeroBannerAction>
            <ButtonsContainer align="left" mobileFullSize>
              <Button
                variant="primary"
                inverted
                href={brochuresUrl}
                data-content-name="hero"
                dataContentPiece="view_the_brochures"
              >
                <Trans>View the Brochures</Trans>
              </Button>

              <Button
                variant="accent"
                renderAs="button"
                onClick={onViewVoyagesClick}
                data-content-name="hero"
                dataContentPiece="view_voyages"
              >
                <Trans>View Voyages</Trans>
              </Button>
            </ButtonsContainer>
          </HeroBannerAction>
        </HeroBannerRightPanel>
      </HeroBanner>

      <SectionContainer
        spacingTop="regular"
        spacingBottom="condensed"
        backgroundColor="white"
        dataContentName="description_text"
      >
        <DescriptionTextColumn>
          <DescriptionTextColumnLeadIn>
            {currentVersion.description?.leadIn?.localized}
          </DescriptionTextColumnLeadIn>
          <DescriptionTextColumnContent>
            <DescriptionTextColumnContentLeft>
              <TextBox
                fontSize="medium"
                content={currentVersion.description?.content?.localized}
              />
            </DescriptionTextColumnContentLeft>

            <DescriptionTextColumnContentRight>
              {filteredSlideshow && (
                <React.Suspense>
                  <ResponsiveSlideshow
                    items={filteredSlideshow}
                    variant="gray"
                    fullscreen
                    dataContentName="slideshow"
                  />
                </React.Suspense>
              )}
            </DescriptionTextColumnContentRight>
          </DescriptionTextColumnContent>
        </DescriptionTextColumn>
      </SectionContainer>

      {hasSpecialFeatureContent && (
        <SectionContainer
          backgroundColor="white"
          //spacingTop={}
          spacingBottom="condensed"
          dataContentName="ship_special-features"
        >
          <ServiceRows>
            {currentVersion.specialFeatures?.map(
              (specialFeatureContent, index) =>
                specialFeatureContent && (
                  <ServiceRow
                    key={index}
                    variant={index % 2 ? "right" : "left"}
                    backgroundColor="neutral05"
                  >
                    {specialFeatureContent.images && (
                      <ServiceRowImages
                        items={specialFeatureContent.images}
                        fallbackAlt={specialFeatureContent.title?.localized}
                      />
                    )}
                    <ServiceRowTitle>
                      {specialFeatureContent.title?.localized}
                    </ServiceRowTitle>
                    {specialFeatureContent.description && (
                      <ServiceRowContent>
                        <BlockContent
                          value={specialFeatureContent.description.localized}
                        />
                      </ServiceRowContent>
                    )}
                    <ServiceRowFooter>
                      <ButtonsContainer align="left">
                        {specialFeatureContent.ctaButtons &&
                          specialFeatureContent.ctaButtons.length > 0 && (
                            <React.Fragment>
                              {specialFeatureContent.ctaButtons.map((b) => (
                                <PageBuilderButtonCTA
                                  key={b?._key}
                                  defaultButtonVariant="text"
                                  {...b}
                                />
                              ))}
                            </React.Fragment>
                          )}
                      </ButtonsContainer>
                    </ServiceRowFooter>
                  </ServiceRow>
                )
            )}
          </ServiceRows>
        </SectionContainer>
      )}

      <React.Suspense>
        {isLastMinuteOfferActive && (
          <ShipCruisesSlider
            shipName={shipName}
            promosContent={props.data.cruisePromosContent}
            shipsContent={props.data.shipsContent}
            backgroundColor="neutral01"
            offerCodes={lastMinuteOfferCodes}
          />
        )}
      </React.Suspense>

      <div id="fyc">
        <FindYourCruise
          id="fyc"
          showTitle
          title={<Trans>Find {shipName} Voyages</Trans>}
          titleAlign="left"
          backgroundColor="neutral10"
          initialSearchQuery={fycQuery}
          resultsPageSize={9}
          showExpeditionFilter={true}
          specialTypeFilter={undefined}
          showDestinationFilter={true}
          showShipFilter={false}
          showDurationFilter={true}
          showPromosFilter={true}
          showFeaturesFilter={true}
          showCruiseEventsFilter={true}
          firstLastDepartureCruiseDates={
            props.data.firstLastDepartureCruiseDates
          }
          promosContent={props.data.cruisePromosContent}
          shipsContent={props.data.shipsContent}
          cruisesTagContent={props.data.cruisesTagContent}
          themedCruisesContent={props.data.themedCruisesContent}
        />
      </div>

      {showVersionsTabs ? (
        <React.Fragment>
          {ship.newVersionContent?.localized && (
            <SectionContainer
              spacingTop="compact"
              spacingBottom="condensed"
              backgroundColor="neutral05"
            >
              <TextBox
                content={ship.newVersionContent.localized}
                textAlign="center"
                fontSize="medium"
                boxed
              />
            </SectionContainer>
          )}
          <SectionContainer
            backgroundColor="neutral05"
            spacingTop="compact"
            spacingBottom={undefined}
          >
            {" "}
          </SectionContainer>
          <Tabs
            data-content-name="tab"
            selectedIndex={tabIndex}
            onSelect={setTabIndex}
          >
            <TabList allBreakpointsSticky backgroundColor="neutral05">
              {versions.map((v, index) => {
                const validityDate = validityDates[index];
                return (
                  <Tab
                    key={v?._key}
                    backgroundColor="neutral05"
                    variant="wide"
                    underline
                    data-content-piece={`ship_version_tab_${v.validFrom}`}
                  >
                    {validityDateLabel(validityDate, language)}
                  </Tab>
                );
              })}
            </TabList>

            {versions.map((v) => (
              <TabPanel
                key={v?._key}
                data-content-name={`ship_version_content_${v.validFrom}`}
              >
                <ShipVersionContent
                  version={v}
                  shipName={shipName || ""}
                  openModal={setOpenModal}
                  spacingTop={undefined}
                />
              </TabPanel>
            ))}
          </Tabs>
        </React.Fragment>
      ) : (
        <ShipVersionContent
          version={currentVersion}
          shipName={shipName || ""}
          openModal={setOpenModal}
          spacingTop="compact"
        />
      )}

      <Slice allowEmpty alias="subscribe-to-newsletter" />

      {openedModal && openedModal.type == "suite" && (
        <React.Suspense fallback={<div></div>}>
          <ShipSuiteModal
            modal={{
              isOpen: !!openedModal,
              onRequestClose: () => setOpenModal(undefined)
            }}
            shipPath={props.location.pathname}
            suiteCode={openedModal.suiteCode}
            shipVersionKey={openedModal.versionKey}
          />
        </React.Suspense>
      )}
      {openedModal &&
        (openedModal.type == "facility" ||
          openedModal.type == "diningArea") && (
          <React.Suspense fallback={<div></div>}>
            <ShipFacilityModal
              modal={{
                isOpen: !!openedModal,
                onRequestClose: () => setOpenModal(undefined)
              }}
              type={openedModal.type}
              content={openedModal.content}
            />
          </React.Suspense>
        )}
    </React.Fragment>
  );
};

export const Head = (props: HeadProps<ShipPageProps, PageContext>) => {
  const { data, pageContext } = props;

  return (
    <GatsbyHeadOpenGraph
      path={props.location.pathname}
      alternateLinks={pageContext.alternateLinks}
      languageCode={pageContext.languageCode}
      openGraph={data.ship.metadata?.openGraph}
      excludeFromSitemap={data.ship.metadata?.excludeFromSitemap}
    />
  );
};

export const query = graphql`
  fragment ShipPageVersion on SanityShipVersion {
    _id
    _key
    validFrom
    shipNotReadyDisclaimer {
      localized
    }

    suites {
      _key
      codes
      card {
        ...AsCard
      }
    }

    facilities {
      _key
      card {
        ...AsCard
      }

      ...ShipFacilityModal
    }

    diningAreas {
      _key
      card {
        ...AsCard
      }

      ...ShipDiningAreaModal
    }

    heroImage: image {
      ...HeroResponsiveImage
    }

    brochureGroup {
      fullPath
    }

    description {
      content {
        localized
      }
      leadIn {
        localized
      }
    }

    slideshow {
      ...SlideshowWithVideo
    }

    specialFeatures {
      title {
        localized
      }
      description {
        localized
      }
      ctaButtons {
        ...ButtonCTA
      }

      images {
        __typename
        ... on SanityImageWithAlt {
          ...ImageWithAlt
        }

        ... on SanityVideoWithPosterImage {
          ...VideoWithPosterImage
        }
      }
    }

    lateralViewImage {
      ...Image
    }
    deckPlanDescription {
      localized
    }
    shipType
    suiteCount
    passengerCount
    crewCount
    buildYear
    lastRefurbishmentYear
    tonnage
    diningOptions

    deckPlanPdf {
      asset {
        url
      }
    }
  }

  query Ship($id: String!) {
    rabPagePath
    firstLastDepartureCruiseDates: firstLastDepartureCruiseDates {
      minYearMonth
      maxYearMonth
    }
    cruisePromosContent {
      ...FYCPromosContent
    }
    shipsContent {
      ...FYCShipsContent
    }
    themedCruisesContent {
      ...FYCThemedCruisesContent
    }
    cruisesTagContent {
      ...FYCCruisesTagContent
    }

    ship: sanityShip(id: { eq: $id }) {
      metadata {
        excludeFromSitemap
        openGraph {
          ...OpenGraphMetaTags
        }
      }

      name
      newVersionContent {
        localized
      }
      versions {
        ...ShipPageVersion
      }
    }
  }
`;

export default React.memo(ShipTemplate);
